
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import CreateFirmForm from '../components/CreateFirmForm';
import CreateUserForm from '../components/CreateUserForm';
import firmService from '../services/firmService';
import userService from '../services/userService';
import '../styles/firms.css';

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.message ||
  'Something went wrong.';

const CreateFirmPage = () => {
  const navigate = useNavigate();

  const [createdFirm, setCreatedFirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userCreated, setUserCreated] = useState(false);

  const handleCreateFirm = async (payload) => {
    setSaving(true);
    setError('');

    try {
      // firmService.createFirm() already extracts the firm object.
      const firmResponse = await firmService.createFirm(payload);

      // Support either a direct firm object or a wrapped response.
      const firm =
        firmResponse?.firm ||
        firmResponse?.data?.firm ||
        firmResponse;

      if (!firm?.id) {
        throw new Error(
          'Firm was created, but its ID was not returned. Please verify the backend response.'
        );
      }

      setCreatedFirm(firm);
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (payload) => {
    if (!createdFirm?.id) {
      setError('Create a firm before creating its distributor account.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await userService.createUser({
        ...payload,
        firm_id: String(createdFirm.id),
      });

      setUserCreated(true);
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">BUSINESS MANAGEMENT</p>

          <h1>{createdFirm ? 'Firm Created' : 'Create Firm'}</h1>

          <p>
            {createdFirm
              ? 'Your firm has been registered successfully.'
              : 'Register a firm in the distributor inventory system.'}
          </p>
        </div>

        <Link
          to="/admin/firms"
          className="admin-button admin-button-secondary"
        >
          Back to Firms
        </Link>
      </header>

      {error && (
        <div className="admin-alert admin-alert-error" role="alert">
          {error}
        </div>
      )}

      {!createdFirm ? (
        <section className="admin-section">
          <CreateFirmForm
            onSubmit={handleCreateFirm}
            onCancel={() => navigate('/admin/firms')}
            submitting={saving}
          />
        </section>
      ) : (
        <>
          <section className="admin-section">
            <div className="admin-success-panel">
              <h2>{createdFirm.firm_name}</h2>

              <p>
                Firm code:{' '}
                <strong>{createdFirm.firm_code || '—'}</strong>
              </p>

              <p>
                Firm ID: <strong>{createdFirm.id}</strong>
              </p>

              <p>
                Status: <strong>{createdFirm.status || '—'}</strong>
              </p>
            </div>
          </section>

          {!userCreated ? (
            <section className="admin-section">
              <CreateUserForm
                firms={[createdFirm]}
                initialFirmId={createdFirm.id}
                onSubmit={handleCreateUser}
                submitting={saving}
                onCancel={() =>
                  navigate(`/admin/firms/${createdFirm.id}`)
                }
              />
            </section>
          ) : (
            <section className="admin-section">
              <div className="admin-success-panel">
                <h2>Distributor account created</h2>

                <p>
                  The distributor account has been created successfully.
                  The distributor can sign in using the configured
                  credentials.
                </p>
              </div>
            </section>
          )}

          <div className="admin-page-actions">
            <button
              type="button"
              className="admin-button admin-button-secondary"
              onClick={() => navigate('/admin/firms/create')}
            >
              Create Another Firm
            </button>

            <button
              type="button"
              className="admin-button admin-button-primary"
              onClick={() =>
                navigate(`/admin/firms/${createdFirm.id}`)
              }
            >
              View Firm Details
            </button>
          </div>
        </>
      )}
    </main>
  );
};

export default CreateFirmPage;
